import { useCallback, useEffect } from "react";
import { isEqual } from "lodash";
import window from 'core/includes/window';
import document from 'core/includes/document';
import useSetState from "core/hooks/useSetState";

// https://usehooks.com/useWindowSize/

const getViewportWidth = () => {
    return document?.documentElement?.clientWidth || 1400;
}

const getBreakpoint = (innerWidth: number) => {
    switch (true) {
        case innerWidth > 2200: return '5xl';
        case innerWidth > 1900: return '4xl';
        case innerWidth > 1600: return '3xl';
        case innerWidth > 1440: return 'xxl';
        case innerWidth > 1280: return 'xl';
        case innerWidth > 1024: return 'lg';
        case innerWidth > 768: return 'md';
        case innerWidth > 568: return 'sm';
        default: return 'xs'
    }
}

const DEFAULT_STATE = {
    width: window.innerWidth,
    height: window.innerHeight,
    viewPortWidth: getViewportWidth(),
    breakpoint: getBreakpoint(window.innerWidth),
};

const useWindowSize = () => {
    // Initialize state with undefined width/height so server and client renders match
    // Learn more here: https://joshwcomeau.com/react/the-perils-of-rehydration/
    const [windowSize, setWindowSize] = useSetState(DEFAULT_STATE);

    const getCurrentWindowSize = useCallback(() => {
        return {
            width: window.innerWidth,
            height: window.innerHeight,
            viewPortWidth: getViewportWidth(),
            breakpoint: getBreakpoint(window.innerWidth),
        };
    }, []);

    useEffect(() => {
        // Handler to call on window resize
        function handleResize() {
            // Set window width/height to state
            setWindowSize(getCurrentWindowSize());
        }

        // Add event listener
        window.addEventListener("resize", handleResize);

        // Call handler right away so state gets updated with initial window size
        handleResize();

        // Remove event listener on cleanup
        return () => window.removeEventListener("resize", handleResize);
    }, [getCurrentWindowSize, setWindowSize]);

    useEffect(() => {
        const currentWindowSize = getCurrentWindowSize();

        if (!isEqual(windowSize, currentWindowSize)) {
            setWindowSize(currentWindowSize);
        }
    }, [windowSize, getCurrentWindowSize, setWindowSize]);

    return windowSize;
}

export default useWindowSize;
